import { isDiscoveryFilterKey, isPageBlockAudience, isPageBlockContentKind, isPageBlockFilterMatch, isPageBlockFilterMode, isPageBlockSortPreset, isPageBlockType, isPlainObject, } from "@next-platform/packages-ts-types";
import { PageBlockSkipReason, PageUnavailableReason } from "./types";
function isNonEmptyArray(value) {
    return value.length > 0;
}
function resolveAudiences(value) {
    if (value === undefined) {
        return undefined;
    }
    if (!Array.isArray(value) || !value.every(isPageBlockAudience) || !isNonEmptyArray(value)) {
        return null;
    }
    return [...value];
}
function resolveFilterValues(value) {
    if (!Array.isArray(value) || !value.every((filterValue) => typeof filterValue === "string") || !isNonEmptyArray(value)) {
        return null;
    }
    return [...value];
}
function resolveFilters(value) {
    if (value === undefined) {
        return undefined;
    }
    if (!Array.isArray(value)) {
        return null;
    }
    const filters = [];
    for (const filter of value) {
        if (!isPlainObject(filter) ||
            !isDiscoveryFilterKey(filter["filterKey"]) ||
            !isPageBlockFilterMode(filter["mode"]) ||
            !isPageBlockFilterMatch(filter["match"])) {
            return null;
        }
        const values = resolveFilterValues(filter["values"]);
        if (values === null) {
            return null;
        }
        filters.push({
            filterKey: filter["filterKey"],
            mode: filter["mode"],
            values,
            match: filter["match"],
        });
    }
    return filters;
}
function resolvePerPage(value) {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== "number") {
        return null;
    }
    return value;
}
function resolveParameters(value) {
    if (!isPlainObject(value) ||
        !isPageBlockContentKind(value["contentKind"]) ||
        typeof value["titleKey"] !== "string" ||
        value["titleKey"].length === 0) {
        return null;
    }
    const filters = resolveFilters(value["filters"]);
    const perPage = resolvePerPage(value["perPage"]);
    if (filters === null || perPage === null || (value["sortPreset"] !== undefined && !isPageBlockSortPreset(value["sortPreset"]))) {
        return null;
    }
    return {
        contentKind: value["contentKind"],
        titleKey: value["titleKey"],
        ...(filters === undefined ? {} : { filters }),
        ...(value["sortPreset"] === undefined ? {} : { sortPreset: value["sortPreset"] }),
        ...(perPage === undefined ? {} : { perPage }),
    };
}
function resolveBlock(value, blockKey) {
    if (!isPlainObject(value)) {
        return { status: "skipped", reason: PageBlockSkipReason.INVALID };
    }
    if (!isPageBlockType(value["type"])) {
        return { status: "skipped", reason: PageBlockSkipReason.UNSUPPORTED_TYPE };
    }
    const audiences = resolveAudiences(value["audiences"]);
    if (audiences === null) {
        return { status: "skipped", reason: PageBlockSkipReason.INVALID };
    }
    const parameters = resolveParameters(value["parameters"]);
    if (parameters === null) {
        return { status: "skipped", reason: PageBlockSkipReason.INVALID_PARAMETERS };
    }
    return {
        status: "resolved",
        block: {
            key: blockKey,
            type: value["type"],
            ...(audiences === undefined ? {} : { audiences }),
            parameters,
        },
    };
}
/**
 * Resolves one page from already-loaded application settings without reading
 * global state, performing I/O or mutating the source configuration.
 */
export default function resolvePageConfiguration({ settings, pageKey }) {
    const page = settings.pages?.[pageKey];
    if (page === undefined) {
        return {
            status: "unavailable",
            pageKey,
            reason: PageUnavailableReason.MISSING,
            skippedBlocks: [],
        };
    }
    if (!isPlainObject(page) ||
        !Array.isArray(page["blocks"]) ||
        page["blocks"].length === 0 ||
        !page["blocks"].every((blockKey) => typeof blockKey === "string")) {
        return {
            status: "unavailable",
            pageKey,
            reason: PageUnavailableReason.MALFORMED,
            skippedBlocks: [],
        };
    }
    const blocks = [];
    const skippedBlocks = [];
    const seenBlockKeys = new Set();
    const seenTitleKeys = new Set();
    for (const blockKey of page["blocks"]) {
        if (seenBlockKeys.has(blockKey)) {
            skippedBlocks.push({
                blockKey,
                reason: PageBlockSkipReason.DUPLICATE,
            });
            continue;
        }
        seenBlockKeys.add(blockKey);
        const block = settings.blocks?.[blockKey];
        if (block === undefined) {
            skippedBlocks.push({
                blockKey,
                reason: PageBlockSkipReason.MISSING,
            });
            continue;
        }
        const resolution = resolveBlock(block, blockKey);
        if (resolution.status === "skipped") {
            skippedBlocks.push({ blockKey, reason: resolution.reason });
            continue;
        }
        const titleKey = resolution.block.parameters.titleKey;
        if (seenTitleKeys.has(titleKey)) {
            skippedBlocks.push({
                blockKey,
                reason: PageBlockSkipReason.DUPLICATE_TITLE,
            });
            continue;
        }
        seenTitleKeys.add(titleKey);
        blocks.push(resolution.block);
    }
    if (blocks.length === 0) {
        return {
            status: "unavailable",
            pageKey,
            reason: PageUnavailableReason.NO_SUPPORTED_BLOCKS,
            skippedBlocks,
        };
    }
    return {
        status: "available",
        pageKey,
        blocks,
        skippedBlocks,
    };
}
